<template>
  <v-container class="registration-tunnel fill-height" fluid>
    <v-row align="center" justify="center" style="min-width: 80vw;">
      <v-col cols="12" sm="10" md="8" lg="6" xl="5">
        <v-card class="pa-6 elevation-8">
          <div class="text-center mb-6">
            <img src="@/assets/images/logo.svg" alt="Logo" style="max-width: 200px;" />
          </div>

          <!-- Chargement -->
          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" :size="60" :width="6" class="mb-4" />
            <p class="text--secondary">{{ $t('loading') }}</p>
          </div>

          <!-- États pleine page : le lien ne mène pas à un dossier à remplir -->
          <div v-else-if="fullPage" class="text-center py-8">
            <v-icon size="80" :color="fullPage.color" class="mb-4">{{ fullPage.icon }}</v-icon>
            <h2 class="text-h6 mb-4">{{ $t(`onboarding.tunnel.state.${fullPage.key}.title`) }}</h2>
            <p class="text--secondary">{{ $t(`onboarding.tunnel.state.${fullPage.key}.message`) }}</p>
          </div>

          <template v-else-if="view">
            <!-- Accueil : qui invite, ce qui va être demandé -->
            <div v-if="step === 'welcome'" class="text-center py-4">
              <v-icon size="72" color="primary" class="mb-4">mdi-account-plus-outline</v-icon>
              <h1 class="text-h5 mb-3">{{ $t('onboarding.tunnel.welcome.title', { organization: organizationName }) }}</h1>
              <p class="text--secondary mb-6">{{ $t('onboarding.tunnel.welcome.message') }}</p>

              <v-list dense class="text-left mx-auto mb-6" style="max-width: 420px;">
                <v-list-item v-for="(name, index) in view.steps" :key="name">
                  <v-list-item-avatar size="28" color="primary" class="white--text">{{ index + 1 }}</v-list-item-avatar>
                  <v-list-item-content>{{ $t(`onboarding.tunnel.step.${stepLabel(name)}`) }}</v-list-item-content>
                </v-list-item>
              </v-list>
              <p v-if="view.requiresApproval" class="text-caption text--secondary mb-6">
                {{ $t('onboarding.tunnel.welcome.approval', { organization: organizationName }) }}
              </p>

              <v-btn color="primary" small @click="step = 'you'">{{ $t('onboarding.tunnel.welcome.start') }}</v-btn>
            </div>

            <!-- Vous -->
            <v-form v-else-if="step === 'you'" ref="form" v-model="valid" @submit.prevent="submit">
              <h2 class="text-h6 mb-4">{{ $t('onboarding.tunnel.step.you') }}</h2>

              <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>

              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.firstname" :label="$t('onboarding.tunnel.field.firstname')" :rules="[required]" outlined dense />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.lastname" :label="$t('onboarding.tunnel.field.lastname')" :rules="[required]" outlined dense />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field :value="view.email" :label="$t('email')" outlined dense readonly disabled />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field v-model="form.phone" :label="$t('onboarding.tunnel.field.phone')" outlined dense />
                </v-col>
              </v-row>

              <div class="text-subtitle-2 mt-2 mb-1">{{ $t('onboarding.tunnel.field.postal-address') }}</div>
              <p class="text-caption text--secondary mb-3">{{ $t('onboarding.tunnel.field.postal-address-why') }}</p>
              <tunnel-address-field v-model="form.postalAddress" />

              <!-- Ce que le type de membre demande en plus (matricule, site…) : sans eux, la fiche du membre reste incomplète. -->
              <tunnel-custom-fields v-model="form.customFields" :fields="view.customFields || []" :organization="organizationName" />

              <div class="d-flex mt-4">
                <v-btn small text @click="step = 'welcome'">{{ $t('onboarding.tunnel.back') }}</v-btn>
                <v-spacer />
                <v-btn small color="primary" type="submit" :loading="submitting" :disabled="!valid">{{ $t('onboarding.tunnel.continue') }}</v-btn>
              </div>
            </v-form>

            <!-- Où installer la borne à domicile : à l'adresse postale, ou ailleurs. Envoyé avec « Vous ». -->
            <v-form v-else-if="step === 'location'" ref="locationForm" v-model="locationValid" @submit.prevent="submit">
              <h2 class="text-h6 mb-2">{{ $t('onboarding.tunnel.step.location') }}</h2>
              <p class="text--secondary mb-4">{{ $t('onboarding.tunnel.location.explained', { organization: organizationName }) }}</p>

              <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>

              <v-radio-group v-model="installation.mode" class="mt-0">
                <v-radio value="postal">
                  <template #label>
                    <div>
                      <div>{{ $t('onboarding.tunnel.location.at-postal-address') }}</div>
                      <div class="text-caption text--secondary">{{ postalAddressLine }}</div>
                    </div>
                  </template>
                </v-radio>
                <v-radio value="other" :label="$t('onboarding.tunnel.location.elsewhere')" />
              </v-radio-group>

              <tunnel-address-field v-if="installation.mode === 'other'" v-model="installation.address" />
              <p class="text-caption text--secondary">{{ $t('onboarding.tunnel.location.next') }}</p>

              <div class="d-flex mt-4">
                <v-btn small text @click="step = 'you'">{{ $t('onboarding.tunnel.back') }}</v-btn>
                <v-spacer />
                <v-btn small color="primary" type="submit" :loading="submitting" :disabled="installation.mode === 'other' && !locationValid">{{ $t('onboarding.tunnel.continue') }}</v-btn>
              </div>
            </v-form>

            <!-- Moyen de paiement : mandat SEPA ou carte, selon ce que l'organisation autorise -->
            <tunnel-payment-method
                v-else-if="step === 'payment-method'"
                :token="token"
                :holder-name="holderName"
                :email="view.email"
                :instruments="view.paymentInstruments || []"
                :deposit-failure="view.deposit && view.deposit.status === 'failed' ? view.deposit.failureReason : null"
                :organization="organizationName"
                :billing="view.billing"
                :deposit="view.deposit"
                :requires-approval="!!view.requiresApproval"
                @collected="collected"
            />

            <!-- Acompte : prélevé sur le moyen de paiement donné, au clic -->
            <!-- Acompte : payé ici, ou — devant une approbation — autorisé ici et prélevé à l'acceptation -->
            <tunnel-deposit
                v-else-if="step === 'deposit'"
                :token="token"
                :organization="organizationName"
                :deposit="view.deposit"
                :authorization="view.depositAuthorization"
                :payment-method="paymentMethodLabel"
                @updated="collected"
            />

            <!-- Confirmation : ce qui se passe ensuite -->
            <div v-else-if="step === 'done'" class="text-center py-6">
              <v-icon size="80" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
              <h2 class="text-h6 mb-3">{{ $t('onboarding.tunnel.done.title') }}</h2>
              <p v-if="!waiting || awaitsTheOrganization" class="text--secondary">
                {{ $t(`onboarding.tunnel.done.next.${nextKey}`, { organization: organizationName, amount: depositAmount }) }}
              </p>

              <!-- Page d'attente : une fois validé, où en est le dossier, et la pose s'il y en a une. -->
              <v-timeline v-else dense align-top class="text-left mx-auto mt-4" style="max-width: 420px;">
                <v-timeline-item
                    v-for="stage in waiting"
                    :key="stage.key"
                    small
                    :color="stage.done ? 'success' : (stage.current ? 'primary' : 'grey lighten-1')"
                    :icon="stage.done ? 'mdi-check' : null"
                    fill-dot
                >
                  <div :class="{ 'font-weight-bold': stage.current, 'text--secondary': !stage.done && !stage.current }">
                    {{ $t(`onboarding.tunnel.waiting.${stage.key}`, stage.params || {}) }}
                  </div>
                  <v-btn v-if="stage.key === 'vehicle' && stage.current && view.vehicle.url" small color="primary" class="mt-2" :href="view.vehicle.url">
                    <v-icon left small>mdi-car-electric</v-icon>{{ $t('onboarding.tunnel.waiting.vehicle-button') }}
                  </v-btn>
                </v-timeline-item>
              </v-timeline>
            </div>
          </template>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import RegistrationRepository from '@repository/RegistrationRepository'
import TunnelCustomFields from './TunnelCustomFields.vue'
import TunnelAddressField from './TunnelAddressField.vue'
import TunnelDeposit from './TunnelDeposit.vue'
import TunnelPaymentMethod from './TunnelPaymentMethod.vue'

const LOCALES = ['fr', 'en', 'nl']

export default {
  name: 'RegistrationTunnel',

  components: { TunnelAddressField, TunnelCustomFields, TunnelDeposit, TunnelPaymentMethod },

  data() {
    return {
      loading: true,
      submitting: false,
      view: null,
      linkProblem: null,
      step: 'welcome',
      valid: false,
      locationValid: false,
      error: null,
      installation: {
        mode: 'postal',
        address: { number: '', street: '', additionalInformation: '', zipcode: '', city: '', country: 'FR' },
      },
      form: {
        firstname: '',
        lastname: '',
        phone: '',
        postalAddress: { number: '', street: '', additionalInformation: '', zipcode: '', city: '', country: 'FR' },
        customFields: {},
      },
    }
  },

  computed: {
    token() {
      return this.$route.query.token || ''
    },

    holderName() {
      return `${this.form.firstname} ${this.form.lastname}`.trim()
    },

    organizationName() {
      return this.view?.organization?.name || ''
    },

    /** Le dossier prévoit une borne à domicile : l'adresse de pose se demande après « Vous ». */
    asksInstallationAddress() {
      return (this.view?.steps || []).includes('location')
    },

    postalAddressLine() {
      const a = this.form.postalAddress
      return [[a.number, a.street].filter(Boolean).join(' '), a.additionalInformation, [a.zipcode, a.city].filter(Boolean).join(' ')]
        .filter(Boolean).join(', ')
    },

    /** Un lien faux, périmé, ou un dossier fermé : un écran plein, pas un formulaire. */
    fullPage() {
      if (this.linkProblem === 'onboarding.tunnel.expired-link') return { key: 'expired-link', icon: 'mdi-clock-alert-outline', color: 'error' }
      if (this.linkProblem) return { key: 'invalid-link', icon: 'mdi-link-off', color: 'error' }
      if (this.view?.state === 'cancelled') return { key: 'cancelled', icon: 'mdi-cancel', color: 'red' }
      if (this.view?.state === 'expired') return { key: 'expired', icon: 'mdi-clock-alert-outline', color: 'error' }
      return null
    },

    /** L'acompte a été autorisé dans le tunnel : il part sans la personne, à l'acceptation. */
    depositAuthorized() {
      return !!this.view?.depositAuthorization?.authorizedAt
    },

    /** L'acompte autorisé est parti (ou va partir) sans elle, et elle n'a rien à faire. */
    depositOnItsWay() {
      const status = this.view?.deposit?.status
      return this.depositAuthorized && this.view?.nextGate === 'deposit' && (!status || status === 'processing')
    },

    /** Le moyen de paiement donné, en mots : « votre carte Visa •••• 4242 », « votre compte •••• 1234 ». */
    paymentMethodLabel() {
      const card = this.view?.card
      const mandate = this.view?.mandate
      if (card) return this.$t('onboarding.tunnel.deposit.authorization.on-card', { brand: (card.brand || '').toUpperCase(), last4: card.last4 })
      if (mandate) return this.$t('onboarding.tunnel.deposit.authorization.on-sepa', { last4: mandate.ibanLast4 })
      return this.$t('onboarding.tunnel.deposit.authorization.on-payment-method')
    },

    /**
     * La frise de la page d'attente, dès que la personne a fini sa part : la décision de
     * l'organisation s'il y en a une, l'acompte qui part à l'acceptation, le compte, la pose s'il y
     * en a une (avec son rendez-vous), le service. Null tant qu'elle a encore quelque chose à faire.
     */
    waiting() {
      const status = this.view?.status
      const order = ['validated', 'created', 'awaiting-installation', 'awaiting-vehicle', 'active']
      const organization = this.organizationName
      if (this.view?.nextGate === 'approval' && this.view?.completeForApproval) {
        return [
          { key: 'submitted', done: true },
          { key: 'approval', current: true, params: { organization } },
          ...(this.depositAuthorized ? [{ key: 'deposit-on-approval', params: { amount: this.depositAmount } }] : []),
          { key: 'account' },
          { key: 'active' },
        ]
      }
      if (this.depositOnItsWay) {
        return [
          { key: 'submitted', done: true },
          { key: 'approved', done: true, params: { organization } },
          { key: 'deposit-charging', current: true, params: { amount: this.depositAmount } },
          { key: 'account' },
          { key: 'active' },
        ]
      }
      if (!order.includes(status)) return null

      const reached = order.indexOf(status)
      const install = this.view.installation
      const stages = [
        { key: 'validated', done: true },
        { key: 'account', done: reached >= 1, current: reached === 0 },
      ]
      if (install || status === 'awaiting-installation') {
        const installed = install?.status === 'commissioned' || status === 'active'
        stages.push(install?.scheduledAt && !installed
          ? { key: 'installation-scheduled', current: reached === 2, params: { date: this.$options.filters.formatDate(install.scheduledAt) } }
          : { key: installed ? 'installation-done' : 'installation-pending', done: installed, current: reached === 2 && !installed })
      }
      // Un service véhicule accordé : le compte créé, la personne connecte son véhicule dans l'espace client.
      const vehicle = this.view.vehicle
      if (vehicle && vehicle.service && vehicle.service !== 'none') {
        const connected = vehicle.connected || status === 'active'
        stages.push({ key: connected ? 'vehicle-done' : 'vehicle', done: connected, current: status === 'awaiting-vehicle' })
      }
      stages.push({ key: 'active', done: status === 'active' })

      return stages
    },

    /** Elle a fini sa part : c'est à l'organisation, puis à l'acompte qui part seul — on le lui dit en tête de la frise. */
    awaitsTheOrganization() {
      return ['approval', 'approval-deposit', 'deposit-on-approval'].includes(this.nextKey)
    },

    depositAmount() {
      const deposit = this.view?.deposit
      return deposit ? new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: (deposit.currency || 'eur').toUpperCase() }).format(deposit.amount || 0) : ''
    },

    /** La suite dépend des portes qui restent : payer, attendre l'accord, ou rien. */
    nextKey() {
      if (this.view?.nextGate === 'payment-method') return 'payment-method'
      if (this.view?.nextGate === 'approval') return this.depositAuthorized ? 'approval-deposit' : 'approval'
      if (this.depositOnItsWay) return 'deposit-on-approval'
      if (this.view?.nextGate === 'deposit') return 'deposit'
      return 'validated'
    },
  },

  created() {
    // La langue du lien s'impose avant tout affichage.
    const locale = this.$route.query.locale
    if (LOCALES.includes(locale)) {
      this.$i18n.locale = locale
    }
    this.load()
  },

  methods: {
    required(value) {
      return (!!value && String(value).trim() !== '') || this.$t('onboarding.tunnel.field.required')
    },

    async load() {
      this.loading = true
      try {
        this.apply(await RegistrationRepository.openTunnel(this.token))
      } catch (e) {
        this.linkProblem = e?.response?.data?.messageKey || 'onboarding.tunnel.invalid-link'
      } finally {
        this.loading = false
      }
    },

    apply(view) {
      this.view = view
      const a = view.applicant || {}
      this.form.firstname = a.firstname || this.form.firstname
      this.form.lastname = a.lastname || this.form.lastname
      this.form.phone = a.phone || this.form.phone
      this.form.postalAddress = { ...this.form.postalAddress, ...(a.postalAddress || {}) }
      this.form.customFields = { ...this.defaultCustomFields(view.customFields || []), ...this.form.customFields, ...(a.customFields || {}) }
      if (a.installationAddress && a.installationAddress.street) {
        this.installation = { mode: 'other', address: { ...this.installation.address, ...a.installationAddress } }
      }
      if (!this.$route.query.locale && LOCALES.includes(view.language)) {
        this.$i18n.locale = view.language
      }
      this.step = this.stepAfter(view)
    },

    /**
     * Le tunnel rouvert reprend où la personne s'est arrêtée : ce qui lui reste à faire, ou la fin.
     * Devant l'approbation, il peut lui rester l'autorisation de l'acompte ; après, l'acompte qu'elle
     * a autorisé part sans elle — sauf si sa banque lui demande de le confirmer.
     */
    stepAfter(view) {
      const done = view.completedSteps || []
      if (view.state === 'submitted') return 'done'
      if (!done.includes('you')) return this.step === 'you' ? 'you' : 'welcome'
      if (view.nextGate === 'payment-method') return 'payment-method'
      if (view.nextGate === 'approval') return view.depositAuthorization?.awaited ? 'deposit' : 'done'
      if (view.nextGate === 'deposit') {
        const status = view.deposit?.status
        const onItsWay = !!view.depositAuthorization?.authorizedAt && (!status || status === 'processing')
        return onItsWay ? 'done' : 'deposit'
      }
      return 'done'
    },

    /** Le libellé d'une étape : l'acompte s'autorise quand une approbation le précède. */
    stepLabel(name) {
      return name === 'deposit' && this.view?.depositAuthorization ? 'deposit-authorization' : name
    },

    /** Les valeurs par défaut des champs personnalisés ; un oui/non part toujours avec une valeur. */
    defaultCustomFields(fields) {
      const values = {}
      fields.forEach(field => {
        if (field.default !== null && field.default !== undefined) values[field.id] = field.default
        else if (field.type === 'bool') values[field.id] = false
      })
      return values
    },

    collected(view) {
      this.apply(view)
    },

    async submit() {
      // « Vous » d'abord ; l'adresse de pose, quand il en faut une, part avec lui depuis l'étape suivante.
      if (this.step === 'you') {
        if (!this.$refs.form.validate()) return
        if (this.asksInstallationAddress) {
          this.error = null
          this.step = 'location'
          return
        }
      } else if (this.step === 'location' && this.installation.mode === 'other' && !this.$refs.locationForm.validate()) {
        return
      }
      this.submitting = true
      this.error = null
      try {
        this.apply(await RegistrationRepository.submitApplicant(this.token, {
          ...this.form,
          installationAddress: this.asksInstallationAddress && this.installation.mode === 'other' ? this.installation.address : null,
          language: this.$i18n.locale,
        }))
      } catch (e) {
        const data = e?.response?.data
        const key = data?.messageKey
        this.error = key && this.$te(key) ? this.$t(key, data.parameters || {}) : this.$t('error-occurred')
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>
